import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useEffect } from 'react';
import { FaCheckCircle, FaTimesCircle, FaExclamationTriangle, FaCog } from 'react-icons/fa';
const ServiceHealthCard = ({ serviceName, displayName, healthEndpoint, port, description }) => {
    const [health, setHealth] = useState({ status: 'loading' });
    const checkHealth = async () => {
        const startTime = Date.now();
        try {
            setHealth(prev => ({ ...prev, status: 'loading' }));
            const controller = new AbortController();
            const timeoutId = setTimeout(() => controller.abort(), 5000);
            const response = await fetch(healthEndpoint, {
                method: 'GET',
                signal: controller.signal,
            });
            clearTimeout(timeoutId);
            const responseTime = Date.now() - startTime;
            if (response.ok) {
                const data = await response.json();
                // fuchi経由のヘルスチェックレスポンス形式に対応
                if (data.success && data.data) {
                    const healthData = data.data;
                    setHealth({
                        status: (healthData.status === 'healthy' || healthData.status === 'up') ? 'healthy' : 'unhealthy',
                        responseTime: healthData.responseTime || responseTime,
                        lastChecked: new Date(),
                        error: healthData.error || undefined,
                    });
                }
                else {
                    setHealth({
                        status: 'unhealthy',
                        responseTime,
                        lastChecked: new Date(),
                        error: data.error || 'Invalid response format',
                    });
                }
            }
            else {
                setHealth({
                    status: 'unhealthy',
                    responseTime,
                    lastChecked: new Date(),
                    error: `HTTP ${response.status}`,
                });
            }
        }
        catch (error) {
            const responseTime = Date.now() - startTime;
            setHealth({
                status: 'unhealthy',
                responseTime,
                lastChecked: new Date(),
                error: error instanceof Error ? error.message : 'Unknown error',
            });
        }
    };
    useEffect(() => {
        checkHealth();
        // 30秒ごとに自動チェック
        const interval = setInterval(checkHealth, 30000);
        return () => clearInterval(interval);
    }, [healthEndpoint]);
    const getStatusIcon = () => {
        switch (health.status) {
            case 'healthy':
                return _jsx(FaCheckCircle, { className: "text-green-500 text-xl" });
            case 'unhealthy':
                return _jsx(FaTimesCircle, { className: "text-red-500 text-xl" });
            case 'loading':
                return _jsx(FaCog, { className: "text-gray-400 text-xl animate-spin" });
            default:
                return _jsx(FaExclamationTriangle, { className: "text-yellow-500 text-xl" });
        }
    };
    const getStatusColor = () => {
        switch (health.status) {
            case 'healthy':
                return 'border-green-200 bg-green-50';
            case 'unhealthy':
                return 'border-red-200 bg-red-50';
            case 'loading':
                return 'border-gray-200 bg-gray-50';
            default:
                return 'border-yellow-200 bg-yellow-50';
        }
    };
    const getStatusText = () => {
        switch (health.status) {
            case 'healthy':
                return 'Healthy';
            case 'unhealthy':
                return 'Unhealthy';
            case 'loading':
                return 'Checking...';
            default:
                return 'Unknown';
        }
    };
    return (_jsxs("div", { className: `border rounded-lg p-4 ${getStatusColor()}`, children: [_jsxs("div", { className: "flex items-center justify-between mb-2", children: [_jsxs("div", { className: "flex items-center space-x-3", children: [getStatusIcon(), _jsxs("div", { children: [_jsx("h3", { className: "font-semibold text-gray-900", children: displayName }), _jsx("p", { className: "text-sm text-gray-600", children: serviceName })] })] }), _jsx("div", { className: "text-right", children: _jsx("span", { className: `inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${health.status === 'healthy'
                                ? 'bg-green-100 text-green-800'
                                : health.status === 'unhealthy'
                                    ? 'bg-red-100 text-red-800'
                                    : health.status === 'loading'
                                        ? 'bg-gray-100 text-gray-800'
                                        : 'bg-yellow-100 text-yellow-800'}`, children: getStatusText() }) })] }), description && (_jsx("p", { className: "text-sm text-gray-600 mb-2", children: description })), _jsxs("div", { className: "flex justify-between items-center text-xs text-gray-500", children: [_jsxs("span", { children: ["Port: ", port] }), health.responseTime && (_jsxs("span", { children: ["Response: ", health.responseTime, "ms"] }))] }), health.lastChecked && (_jsxs("div", { className: "text-xs text-gray-500 mt-1", children: ["Last checked: ", health.lastChecked.toLocaleTimeString()] })), health.error && (_jsxs("div", { className: "text-xs text-red-600 mt-1", children: ["Error: ", health.error] })), _jsx("button", { onClick: checkHealth, disabled: health.status === 'loading', className: "mt-2 w-full text-xs bg-white border border-gray-300 rounded px-2 py-1 hover:bg-gray-50 disabled:opacity-50", children: health.status === 'loading' ? 'Checking...' : 'Check Now' })] }));
};
export default ServiceHealthCard;
